import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  postComment,
  readReviewerIdentity,
  writeReviewerIdentity,
} from "./mockupApi";

const formatThreadTimestamp = (entry) => {
  if (!entry?.createdAt) {
    return "";
  }
  const seconds = entry.createdAt._seconds || entry.createdAt.seconds;
  if (!seconds) {
    return "";
  }
  return new Date(seconds * 1000).toLocaleString();
};

const isAclymateEmail = (email) => /@aclymate\.com$/i.test(email.trim());

const DECISION_CHIP_COLOR = {
  addressed: "default",
  carried_over: "warning",
  flagged_obsolete: "error",
  needs_product_decision: "error",
  deferred_to_build: "info",
};

const DECISION_LABEL = {
  addressed: "Addressed",
  carried_over: "Carried over",
  flagged_obsolete: "Possibly obsolete",
  needs_product_decision: "Needs your decision",
  deferred_to_build: "Recorded for build phase",
};

const buildStatusChipProps = (comment) => {
  const decision = comment.mockupResolution?.decision;
  if (decision && DECISION_LABEL[decision]) {
    return { label: DECISION_LABEL[decision], color: DECISION_CHIP_COLOR[decision] };
  }
  return {
    label: comment.status,
    color: comment.status === "resolved" ? "default" : "primary",
  };
};

const ThreadEntry = ({ entry }) => (
  <Stack spacing={0.5} sx={{ py: 1 }}>
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="textSecondary">
        {entry.authorDisplayName || entry.author}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {formatThreadTimestamp(entry)}
      </Typography>
    </Stack>
    <Typography variant="body2">{entry.text}</Typography>
  </Stack>
);

const CommentDetail = ({ comment, hillId, onReplied }) => {
  const initialIdentity = readReviewerIdentity();
  const [name, setName] = useState(initialIdentity.name);
  const [email, setEmail] = useState(initialIdentity.email);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    Boolean(reply.trim()) &&
    Boolean(name.trim()) &&
    isAclymateEmail(email) &&
    !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await postComment({
        hillId,
        parentCommentId: comment.id,
        comment: reply.trim(),
        reviewerName: name.trim(),
        reviewerEmail: email.trim(),
      });
      writeReviewerIdentity({ name: name.trim(), email: email.trim() });
      setReply("");
      onReplied();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          label={comment.anchor?.scenarioName || comment.anchor?.scenarioId}
          size="small"
        />
        <Typography variant="caption" color="textSecondary">
          {comment.anchor?.screenTitle || comment.anchor?.screenRoute}
        </Typography>
      </Stack>
      {comment.anchor?.screenshotThumbUrl && (
        <Box
          component="img"
          src={comment.anchor.screenshotThumbUrl}
          alt="Snip"
          sx={{ width: "100%", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
        />
      )}
      {comment.mockupResolution && (
        <Stack spacing={0.5}>
          <Chip size="small" {...buildStatusChipProps(comment)} sx={{ alignSelf: "flex-start" }} />
          {comment.mockupResolution.rationale && (
            <Typography variant="body2" color="textSecondary">
              {comment.mockupResolution.rationale}
            </Typography>
          )}
        </Stack>
      )}
      <Divider />
      <Stack divider={<Divider />}>
        {(comment.thread || []).map((entry, index) => (
          <ThreadEntry key={index} entry={entry} />
        ))}
      </Stack>
      <Divider />
      <TextField
        label="Reply"
        multiline
        rows={3}
        value={reply}
        onChange={(event) => setReply(event.target.value)}
        fullWidth
      />
      <TextField
        label="Your name"
        size="small"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <TextField
        label="Your @aclymate.com email"
        size="small"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        error={Boolean(email) && !isAclymateEmail(email)}
      />
      <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
        {submitting ? "Sending…" : "Reply"}
      </Button>
    </Stack>
  );
};

const CommentDrawer = ({
  open,
  onClose,
  selectedComment,
  comments,
  hillId,
  onSelect,
  onReplied,
}) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    sx={{ zIndex: (theme) => theme.zIndex.tooltip + 1 }}
    PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5 }}>
      <Typography variant="h6">Comments on this screen</Typography>
      <IconButton onClick={onClose} aria-label="Close drawer">
        <CloseIcon />
      </IconButton>
    </Stack>
    <Divider />
    {selectedComment ? (
      <CommentDetail
        comment={selectedComment}
        hillId={hillId}
        onReplied={onReplied}
      />
    ) : (
      <Stack divider={<Divider />}>
        {comments.length === 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
            No comments yet on this screen.
          </Typography>
        )}
        {comments.map((comment) => (
          <Box
            key={comment.id}
            onClick={() => onSelect(comment)}
            sx={{ p: 1.5, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="textSecondary">
                {comment.thread?.[0]?.authorDisplayName || "Reviewer"}
              </Typography>
              <Chip size="small" {...buildStatusChipProps(comment)} />
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {comment.thread?.[0]?.text?.slice(0, 120) || ""}
            </Typography>
          </Box>
        ))}
      </Stack>
    )}
  </Drawer>
);

export default CommentDrawer;
